import "vite/modulepreload-polyfill";
import "@vitejs/plugin-react/preamble";

import { StrictMode } from "react";
import { hydrateRoot, createRoot, Root } from "react-dom/client";
import App from "./common/App";
import HtmlPage from "./common/HtmlPage";
import "./index.css";
import * as Sentry from "@sentry/react";
import * as SentryIntegrations from "@sentry/browser";
import { logger } from "./common/logger";
import { AppSignal } from "./common/browser/state";
import { createRelayEnvironment } from "./common/browser/relay";
import { createRouter } from "./common/browser/router";
import { createProviders } from "./common/browser/react";
import { createRedirect } from "./common/browser/redirect";
import { initPostHog, posthog } from "./common/posthog";

startSentry();
initPostHog(logger);
document.addEventListener("DOMContentLoaded", async () => {
  const container = document.getElementById("root");
  if (container === null) {
    throw new Error("Invalid app");
  }

  logger.debug("Reading app state...");
  const app = AppSignal.readLocal();
  window.addEventListener("beforeunload", () => app.flush());

  let shouldHydrate;
  if (app.detectCookieMismatch()) {
    shouldHydrate = false;
    logger.debug("Page won't be hydrated because of cookie mismatch");
  } else {
    shouldHydrate = !document.querySelector('meta[name="x-aqora-error"]');
  }

  app.cleanStaleTokens();
  // Subscribed once, ahead of the restart below: a sync re-subscribed by each
  // restart would run after it and miss every change of user but the first.
  app.syncLocalStorage();

  let cleanup: ((restart: boolean) => void) | undefined;
  let root: Root | undefined;

  if (shouldHydrate) {
    [root, cleanup] = await hydrateApp(app);
  } else {
    cleanup = startApp(container, app);
  }

  app.asUserId().subscribe({
    // restart application when user id changes
    next: async () => {
      cleanup?.(true);
      cleanup = startApp(root ?? container, app);
    },

    complete: () => {
      cleanup?.(false);
      cleanup = undefined;
    },
  });
});

async function hydrateApp(app: AppSignal) {
  const cleanups: Array<() => void> = [];
  cleanups.push(app.autoExpires());

  const [relay, disposeRelay] = createRelayEnvironment(app, app.userId, true);
  cleanups.push(disposeRelay);

  const router = createRouter(relay, true);
  cleanups.push(router.dispose.bind(router));

  logger.debug("App hydrating...");
  await Promise.all(
    router.state.matches.map(({ route }) => {
      if (typeof route.lazy === "function") {
        return route.lazy?.();
      } else if (route.lazy) {
        return Promise.all(Object.values(route.lazy));
      }
    }),
  );

  const [setLocation, takeLocation] = createRedirect();

  const node = (
    <StrictMode>
      <HtmlPage>
        <App {...createProviders(app, posthog, relay, router, setLocation)} />
      </HtmlPage>
    </StrictMode>
  );

  const root = hydrateRoot(document, node);

  const dispose = (restart: boolean) => {
    logger.debug(restart ? "Restarting app..." : "Stopping app...");
    for (const unsub of cleanups.splice(0, cleanups.length).reverse()) {
      unsub();
    }
    const location = takeLocation();
    if (restart && location?.replace) {
      history.replaceState(null, "", location.url);
    } else if (restart && location) {
      history.pushState(null, "", location.url);
    }
  };

  logger.debug("App hydrated!");

  return [root, dispose] as const;
}

function startApp(container: HTMLElement | Root, app: AppSignal) {
  const cleanups: Array<() => void> = [];
  cleanups.push(app.autoExpires());

  const [relay, disposeRelay] = createRelayEnvironment(app, app.userId, false);
  cleanups.push(disposeRelay);

  const router = createRouter(relay, false);
  cleanups.push(router.dispose.bind(router));

  const [setLocation, takeLocation] = createRedirect();

  const node = (
    <App {...createProviders(app, posthog, relay, router, setLocation)} />
  );

  let root;
  if (container instanceof Element) {
    root = createRoot(container);
    root.render(<StrictMode>{node}</StrictMode>);
  } else {
    root = container;
    root.render(
      <StrictMode>
        <HtmlPage>{node}</HtmlPage>
      </StrictMode>,
    );
  }

  logger.debug("App started!");

  return (restart: boolean) => {
    logger.debug(restart ? "Restarting app..." : "Stopping app...");

    if (container instanceof Element) {
      root.unmount();
    }
    for (const unsub of cleanups.splice(0, cleanups.length).reverse()) {
      unsub();
    }

    const location = takeLocation();
    if (restart && location?.replace) {
      history.replaceState(null, "", location.url);
    } else if (restart && location) {
      history.pushState(null, "", location.url);
    }
  };
}

function startSentry(dsn = import.meta.env.VITE_SENTRY_DSN) {
  if (dsn) {
    Sentry.init({
      dsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        SentryIntegrations.captureConsoleIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      tracePropagationTargets: ["localhost", /^https:\/\/aqora\.io/], // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    });
    logger.info("Sentry initialized: %s", dsn);
  } else {
    logger.warn("Sentry DSN not found");
  }
}
